import { ArrowRightIcon, DotsThreeIcon, PlusIcon, SparkleIcon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  type WorkflowStepRenderState,
  WorkflowStepRow,
} from '@/components/ui/inspector/bodies/workflow-tracker/WorkflowStepRow';
import { StatusBadge } from '@/components/ui/status-badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ICON_BUTTON, ITEM_META, MOBILE_HIT_AREA, SECTION_TITLE } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import { campaignService } from '@/services/data/CampaignService';
import type {
  CampaignWithCoverage,
  WorkflowState,
  WorkflowStep,
  WorkflowTemplate,
} from '@/services/data/CampaignService';

interface WorkflowSummarySectionProps {
  campaign: CampaignWithCoverage;
  onOpenWorkflowLibrary?: () => void;
  /**
   * When true, render the FULL step list (Claude Code "Progress" pattern)
   * instead of just a 1-line "Step N of M" summary. Used by the project
   * hub on /create where the user explicitly opens the rail to see
   * progress. The /projects/:id detail rail keeps the compact summary
   * because it's surfaced alongside other project metadata.
   */
  showSteps?: boolean;
}

// Per-step render-state derivation (mirrors the WorkflowTrackerBody logic).
// Done items are anything before the current step OR present in history with
// status='completed'. The current step renders as awaiting_approval / running
// / failed depending on the workflow's overall status. Future steps are up_next.
function deriveStepState(
  step: WorkflowStep,
  index: number,
  currentIdx: number,
  workflowState: WorkflowState | null,
): WorkflowStepRenderState {
  if (!workflowState) return 'up_next';
  const inHistory = workflowState.history?.find((h) => h.step_id === step.step_id);
  if (inHistory && inHistory.status === 'completed') return 'completed';
  if (index < currentIdx) return 'completed';
  if (index === currentIdx) {
    if (workflowState.status === 'failed') return 'failed';
    if (workflowState.status === 'awaiting_approval') return 'awaiting_approval';
    if (workflowState.status === 'running') return 'running';
    if (workflowState.status === 'completed') return 'completed';
    // Audit pass 6 (2026-05-08): when workflow is not_started, the current
    // step (index 0 by default) needs the "you-are-here" visual cue so the
    // user knows which step kicks off first. Reuse the awaiting_approval
    // disc treatment (ringed circle with center dot) — semantically the
    // workflow IS waiting on user input to begin. Without this, all 4
    // steps render as plain numerics and the panel feels static.
    if (workflowState.status === 'not_started') return 'awaiting_approval';
  }
  return 'up_next';
}

function findCompletedAt(state: WorkflowState | null, step: WorkflowStep): string | null {
  if (!state) return null;
  const entry = state.history?.find((h) => h.step_id === step.step_id);
  if (!entry) return null;
  const ts = entry.completed_at ?? entry.timestamp;
  return typeof ts === 'string' ? ts : null;
}

function statusCopy(state: WorkflowState | null): string | null {
  if (!state) return null;
  switch (state.status) {
    case 'awaiting_approval':
      return 'Awaiting you';
    case 'running':
      return 'Running';
    case 'completed':
      return 'Complete';
    case 'failed':
      return 'Failed';
    case 'not_started':
    default:
      // Audit pass 2: "Ready when you are" was ambiguous (loading?
      // waiting? prompt?). "Awaiting input" is the same semantic but
      // names the actor — the WORKFLOW is waiting on the USER. Pairs
      // cleanly with the running status's "Awaiting you" wording for
      // approval gates.
      return 'Awaiting input';
  }
}

function statusTone(
  state: WorkflowState | null,
): 'success' | 'warning' | 'info' | 'destructive' | 'neutral' | null {
  if (!state) return null;
  switch (state.status) {
    case 'awaiting_approval':
      return 'warning';
    case 'running':
      return 'info';
    case 'completed':
      return 'neutral';
    case 'failed':
      return 'destructive';
    case 'not_started':
    default:
      // Audit pass 2: was null (rendered as plain text). Now returns
      // 'neutral' so the StatusBadge primitive renders for ALL states,
      // not just running/awaiting. Visual consistency across the
      // workflow lifecycle — every state has a pill, no flat-text
      // outlier.
      return 'neutral';
  }
}

/**
 * Workflow read-only summary for the project hub right rail.
 *
 * Three states by status (`workflow_state.status`):
 * 1. **No template assigned** — single inline link "Assign an AI Workflow →"
 *    (replaces the prior dashed-card CTA per Wave 7 subtraction).
 * 2. **Assigned, not_started** — template name + "Step 1 · {action}" + a
 *    primary `Open in chat →` Button + inline `Change · Remove` text-buttons.
 *    Mid-flight swap is intentionally hidden — see (3).
 * 3. **Assigned, running / awaiting / completed / failed** — template name +
 *    "Step N of M · {action}" + StatusBadge + `Open in chat →`. No
 *    change/remove during execution: PM round-table flagged that swapping a
 *    template mid-run has state-clearing implications and shouldn't be a
 *    casual click.
 *
 * The full tracker (asset preview, Approve / Regenerate, etc.) only mounts
 * inside the chat-page Inspector via `WorkflowTrackerBody`. This file is a
 * read surface, not an execution surface.
 */
export const WorkflowSummarySection: React.FC<WorkflowSummarySectionProps> = ({
  campaign,
  onOpenWorkflowLibrary,
  showSteps = false,
}) => {
  const navigate = useNavigate();
  const templateId = campaign.activeWorkflowTemplateId;
  const workflowState = campaign.workflowState;

  const { data: templateResp } = useQuery({
    queryKey: ['workflow_template', templateId],
    queryFn: () =>
      templateId ? campaignService.getWorkflowTemplate(templateId) : Promise.resolve(null),
    enabled: !!templateId,
    staleTime: 60_000,
  });
  const template = (templateResp?.data ?? null) as WorkflowTemplate | null;
  const steps: WorkflowStep[] = template?.steps ?? [];

  const currentStep = workflowState
    ? (steps.find((s) => s.step_id === workflowState.current_step_id) ?? null)
    : null;
  const currentIdx = workflowState
    ? steps.findIndex((s) => s.step_id === workflowState.current_step_id)
    : -1;
  const isFinished = workflowState?.status === 'completed';
  const showChangeRemove = workflowState?.status === 'not_started';

  const openInChat = () => {
    navigate(`/?planId=${campaign.id}`);
  };

  const handleRemoveWorkflow = async () => {
    await campaignService.updateCampaign(campaign.id, {
      activeWorkflowTemplateId: null,
      workflowState: null,
    });
    // Caller invalidates ['campaigns'] via TanStack on next focus / poll.
  };

  // STATE 1 — no template assigned. Mirrors the Instructions/Files sibling
  // pattern exactly: "Workflow" title + a PlusIcon header action (same as
  // Files' "Add file" / Context's "+"), with a clickable placeholder line
  // below (same affordance as InlineFieldEdit's empty-state placeholder).
  if (!templateId) {
    return (
      <section className="p-5">
        <div className="mb-2 flex items-center justify-between">
          <h3 className={SECTION_TITLE}>Workflow</h3>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={onOpenWorkflowLibrary}
                className={cn(ICON_BUTTON, MOBILE_HIT_AREA)}
                aria-label="Assign a workflow"
              >
                <PlusIcon className="size-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Assign a workflow</TooltipContent>
          </Tooltip>
        </div>
        <button
          type="button"
          onClick={onOpenWorkflowLibrary}
          className="-mx-1 -my-0.5 block w-full rounded-sm px-1 py-0.5 text-left text-caption leading-relaxed text-muted-foreground/50 transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/30"
        >
          No workflow assigned
        </button>
      </section>
    );
  }

  // STATE 2/3 — template assigned, render the summary
  const tone = statusTone(workflowState);
  const copy = statusCopy(workflowState);
  const stepLabel = (() => {
    if (!workflowState || !steps.length) return template?.name ?? 'Loading…';
    if (isFinished) return `${steps.length} of ${steps.length} done`;
    return `Step ${Math.max(0, currentIdx) + 1} of ${steps.length}`;
  })();

  // The section now mirrors its siblings (Instructions, Files): a "Workflow"
  // title with a trailing ⋯ overflow for the destructive/rare actions
  // (Change template · Remove), rather than a row of dot-separated text
  // buttons that read as flat copy. The template NAME anchors the section
  // (the /projects/:id breadcrumb shows the PROJECT name, not the workflow's,
  // so this is not a duplicate here). Live state (step + status) sits under
  // it, and a single "Open in chat →" link is the primary affordance.
  //
  // Change / Remove are only offered before the workflow starts running
  // (`showChangeRemove`) — swapping a template mid-run has state-clearing
  // implications the PM round-table flagged as not-a-casual-click.
  //
  // `showSteps` (hub variant on /create) additionally renders the FULL step
  // list via <WorkflowStepRow> (Claude Code "Progress" pattern) and omits the
  // "Open in chat" link — the user is already in chat there.
  const optionsMenu = showChangeRemove ? (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn(ICON_BUTTON, MOBILE_HIT_AREA)}
              aria-label="Workflow options"
            >
              <DotsThreeIcon className="size-4" weight="bold" />
            </button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">Workflow options</TooltipContent>
      </Tooltip>
      <DropdownMenuContent
        align="end"
        sideOffset={6}
        className="w-52 rounded-xl border border-border/60 bg-popover/95 p-1 shadow-elevation-3 backdrop-blur-xl"
      >
        <DropdownMenuItem
          onClick={onOpenWorkflowLibrary}
          className="gap-2 rounded-sm px-2 py-1.5 text-caption"
        >
          <SparkleIcon className="size-4 text-muted-foreground/70" weight="regular" />
          <span>Change template</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => void handleRemoveWorkflow()}
          className="gap-2 rounded-sm px-2 py-1.5 text-caption text-destructive focus:text-destructive"
        >
          <span>Remove workflow</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  ) : null;

  return (
    <section className={cn(showSteps ? 'pt-4 pb-2' : 'p-5')}>
      {/* Section header — matches the Instructions / Files pattern */}
      <div className={cn('flex items-center justify-between', showSteps ? 'mb-2 px-5' : 'mb-3')}>
        <h3 className={SECTION_TITLE}>Workflow</h3>
        {optionsMenu}
      </div>

      {/* Template name — the anchor (not shown in the breadcrumb on this page) */}
      {template?.name && (
        <p className={cn('truncate text-sm font-medium text-foreground/90', showSteps && 'px-5')}>
          {template.name}
        </p>
      )}

      {/* Live state — step + status */}
      <div
        className={cn('mt-1 flex flex-wrap items-center gap-1.5', showSteps && 'px-5', ITEM_META)}
      >
        <span className="tabular-nums">{stepLabel}</span>
        {currentStep && !isFinished && !showSteps && (
          <>
            <span className="text-muted-foreground/40">·</span>
            <span className="truncate">{currentStep.action}</span>
          </>
        )}
        {tone && copy && <StatusBadge tone={tone}>{copy}</StatusBadge>}
      </div>

      {/* Full step list (Claude Code "Progress" pattern) — hub variant */}
      {showSteps && steps.length > 0 && (
        <ol className="mt-2 flex flex-col" aria-label="Workflow steps">
          {steps.map((step, idx) => {
            const renderState = deriveStepState(step, idx, currentIdx, workflowState);
            return (
              <WorkflowStepRow
                key={step.step_id}
                step={step}
                index={idx}
                state={renderState}
                completedAt={findCompletedAt(workflowState, step)}
              />
            );
          })}
        </ol>
      )}

      {/* Primary affordance — route to the chat execution surface. Omitted in
          the hub, where the user is already in chat. */}
      {!showSteps && (
        <button
          type="button"
          onClick={openInChat}
          className="group mt-3 inline-flex items-center gap-1 text-caption font-medium text-foreground/80 transition-colors hover:text-foreground"
        >
          Open in chat
          <ArrowRightIcon
            className="size-3 transition-transform group-hover:translate-x-0.5"
            weight="bold"
          />
        </button>
      )}
    </section>
  );
};
